import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { exact, money } from '@/lib/format';
import { StatusPill } from './StatusPill';

const levelTone = {
  Info: 'text-zinc-400',
  Warning: 'text-amber-300',
  Error: 'text-rose-300',
} as const;

/**
 * The "what happened to this one?" panel: the funds the ATO returned, the step log, and
 * the raw webhook body. The raw body matters — when Ontraport changes what its automation
 * sends, this is the only place that shows it.
 */
export function RunDetail({ runId, onClose }: { runId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.getRun(runId),
    refetchInterval: 10_000,
  });

  if (isLoading || !data) {
    return (
      <aside className="panel p-6 text-sm text-zinc-500">Loading run {runId}…</aside>
    );
  }

  // The contract allows these to be null. The server always sends arrays, but coalescing
  // here means a change on that side can't blank the panel.
  const { summary, receipt } = data;
  const funds = data.funds ?? [];
  const log = data.log ?? [];

  return (
    <aside className="panel flex max-h-[calc(100vh-11rem)] flex-col overflow-hidden">
      <header className="flex items-start justify-between gap-4 border-b border-zinc-800 px-5 py-4">
        <div>
          <h2 className="font-medium text-zinc-100">{summary.contactName || summary.contactEmail}</h2>
          <p className="mt-0.5 font-mono text-xs text-zinc-500">
            run #{summary.id} · FY{summary.taxYear} ·{' '}
            {summary.ontraportContactId ? `contact ${summary.ontraportContactId}` : 'unmatched'}
          </p>
        </div>
        <button className="btn px-2 py-1 text-xs" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="space-y-6 overflow-y-auto px-5 py-4">
        <section className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
          <StatusPill status={summary.status} />
          <span>Queued {exact(summary.createdAt)}</span>
          {summary.completedAt && <span>Finished {exact(summary.completedAt)}</span>}
          {summary.nextAttemptAt && <span>Next try {exact(summary.nextAttemptAt)}</span>}
          {summary.attemptCount > 0 && <span>{summary.attemptCount} attempt(s)</span>}
          {summary.ontraportPushedAt && <span>Written to Ontraport {exact(summary.ontraportPushedAt)}</span>}
        </section>

        <section>
          <h3 className="field-label">Super accounts</h3>
          {funds.length === 0 ? (
            <p className="text-sm text-zinc-500">No accounts recorded.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[26rem] text-left text-sm">
                <thead className="text-xs text-zinc-600">
                  <tr>
                    <th className="py-1 font-medium">Fund</th>
                    <th className="py-1 font-medium">USI</th>
                    <th className="py-1 font-medium">Account</th>
                    <th className="py-1 text-right font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/70">
                  {funds.map((fund, index) => (
                    <tr key={`${fund.usi}-${index}`}>
                      <td className="py-1.5 pr-3 text-zinc-200">{fund.fundName || '—'}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs text-zinc-500">{fund.usi || '—'}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs text-zinc-500">{fund.accountNumber || '—'}</td>
                      <td className="py-1.5 text-right font-mono text-zinc-200">{money(fund.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h3 className="field-label">Step log</h3>
          <ol className="space-y-1.5">
            {log.map((entry, index) => (
              <li key={index} className="flex gap-3 text-sm">
                <span className="shrink-0 font-mono text-xs text-zinc-600">
                  {new Date(entry.at.endsWith('Z') ? entry.at : `${entry.at}Z`).toLocaleTimeString('en-AU')}
                </span>
                <span className={clsx(levelTone[entry.level])}>{entry.message}</span>
              </li>
            ))}
          </ol>
        </section>

        {receipt && (
          <section>
            <h3 className="field-label">Inbound webhook</h3>
            <p className="mb-2 text-xs text-zinc-500">
              {exact(receipt.receivedAt)} · {receipt.contentType || 'unknown type'} · from{' '}
              {receipt.remoteIp || 'unknown'}
              {!receipt.authorised && <span className="text-rose-300"> · rejected: {receipt.rejectionReason}</span>}
            </p>
            <pre className="max-h-56 overflow-auto rounded-md border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs whitespace-pre-wrap text-zinc-400">
              {receipt.rawPayload || '(empty body)'}
            </pre>
          </section>
        )}
      </div>
    </aside>
  );
}
